const ZERO_ADDRESS_TOPIC = `0x${"0".repeat(64)}`;

const ERC1155_URI_INPUTS = [
  { indexed: false, name: "value", type: "string" },
  { indexed: true, name: "id", type: "uint256" },
];

function requireWeb3(web3) {
  if (!web3?.eth?.abi || !web3?.utils) {
    throw new TypeError("A Web3 instance is required to verify the mint receipt");
  }
}

function normalizedAddress(value) {
  return String(value || "").toLowerCase();
}

function decimalTokenId(web3, value) {
  return web3.utils.toBN(value).toString(10);
}

function contractLogs(receipt, contractAddress) {
  const expectedAddress = normalizedAddress(contractAddress);
  if (!/^0x[0-9a-f]{40}$/.test(expectedAddress)) {
    throw new TypeError("A valid NFT contract address is required");
  }
  return (receipt?.logs || []).filter(
    (log) => normalizedAddress(log?.address) === expectedAddress,
  );
}

export function parseErc1155MintMetadata({
  web3,
  receipt,
  contractAddress,
  expectedUris = [],
}) {
  requireWeb3(web3);
  const uriTopic = web3.eth.abi
    .encodeEventSignature("URI(string,uint256)")
    .toLowerCase();
  const events = contractLogs(receipt, contractAddress)
    .filter((log) => normalizedAddress(log?.topics?.[0]) === uriTopic)
    .map((log) => {
      const decoded = web3.eth.abi.decodeLog(
        ERC1155_URI_INPUTS,
        log.data,
        log.topics.slice(1),
      );
      return {
        tokenId: decimalTokenId(web3, decoded.id),
        uri: String(decoded.value || "").trim(),
      };
    });

  if (events.length === 0) {
    throw new Error("The ERC-1155 mint did not emit the required URI event");
  }

  const seen = new Set();
  for (const event of events) {
    if (!event.uri) throw new Error("The ERC-1155 URI event was empty");
    if (seen.has(event.tokenId)) {
      throw new Error(`Duplicate ERC-1155 metadata event for token ${event.tokenId}`);
    }
    seen.add(event.tokenId);
  }

  if (expectedUris.length > 0) {
    if (events.length !== expectedUris.length) {
      throw new Error(
        `Expected ${expectedUris.length} metadata events but received ${events.length}`,
      );
    }
    events.forEach((event, index) => {
      if (event.uri !== String(expectedUris[index] || "").trim()) {
        throw new Error(`On-chain metadata URI mismatch for token ${event.tokenId}`);
      }
    });
  }

  return events;
}

export function parseErc721MintTokenIds({
  web3,
  receipt,
  contractAddress,
  expectedCount,
}) {
  requireWeb3(web3);
  const transferTopic = web3.eth.abi
    .encodeEventSignature("Transfer(address,address,uint256)")
    .toLowerCase();
  const mintLogs = contractLogs(receipt, contractAddress).filter(
    (log) =>
      normalizedAddress(log?.topics?.[0]) === transferTopic &&
      normalizedAddress(log?.topics?.[1]) === ZERO_ADDRESS_TOPIC &&
      log?.topics?.length >= 4,
  );

  if (mintLogs.length === 0) {
    throw new Error("The ERC-721 mint did not emit a zero-address Transfer event");
  }
  if (expectedCount !== undefined && mintLogs.length !== expectedCount) {
    throw new Error(
      `Expected ${expectedCount} ERC-721 mint events but received ${mintLogs.length}`,
    );
  }
  return mintLogs.map((log) => decimalTokenId(web3, log.topics[3]));
}

export function parseErc721MintTokenId({ web3, receipt, contractAddress }) {
  const tokenIds = parseErc721MintTokenIds({
    web3,
    receipt,
    contractAddress,
    expectedCount: 1,
  });
  return tokenIds[0];
}

export function toTokenIdString(tokenId) {
  const value = String(tokenId ?? "").trim();
  if (!/^\d+$/.test(value)) {
    throw new TypeError("The token ID must be a non-negative decimal integer");
  }
  return value.replace(/^0+(?=\d)/, "");
}

export async function assertOnChainMetadataUri({
  web3,
  abi,
  contractAddress,
  method,
  tokenId,
  expectedUri,
}) {
  requireWeb3(web3);
  const contract = new web3.eth.Contract(abi, contractAddress);
  const metadataMethod = contract.methods?.[method];
  if (typeof metadataMethod !== "function") {
    throw new Error(`The NFT contract does not expose ${method}(tokenId)`);
  }
  const actualUri = String(await metadataMethod(tokenId).call()).trim();
  if (actualUri !== String(expectedUri || "").trim()) {
    throw new Error(`The ${method}(tokenId) value does not match the uploaded metadata`);
  }
  return actualUri;
}

export async function assertOnChainMetadataBatch({
  web3,
  abi,
  contractAddress,
  method,
  items,
  concurrency = 4,
}) {
  requireWeb3(web3);
  const queue = Array.from(items || []);
  if (queue.length === 0) throw new Error("No minted tokens were found to verify");
  const contract = new web3.eth.Contract(abi, contractAddress);
  const metadataMethod = contract.methods?.[method];
  if (typeof metadataMethod !== "function") {
    throw new Error(`The NFT contract does not expose ${method}(tokenId)`);
  }
  let cursor = 0;
  const workerCount = Math.max(1, Math.min(Number(concurrency) || 1, queue.length));

  async function worker() {
    while (cursor < queue.length) {
      const index = cursor;
      cursor += 1;
      const item = queue[index];
      const actualUri = String(await metadataMethod(item.tokenId).call()).trim();
      if (actualUri !== String(item.uri || "").trim()) {
        throw new Error(`On-chain metadata URI mismatch for token ${item.tokenId}`);
      }
    }
  }

  await Promise.all(Array.from({ length: workerCount }, worker));
}

function requireHttpsUrl(value, field) {
  let parsed;
  try {
    parsed = new URL(String(value || "").trim());
  } catch (_) {
    throw new Error(`${field} is not a valid public URL`);
  }
  if (parsed.protocol !== "https:") {
    throw new Error(`${field} must use HTTPS for cross-explorer compatibility`);
  }
  return parsed.toString();
}

async function fetchPublicAsset(fetchImpl, url, method) {
  const response = await fetchImpl(url, {
    method,
    headers: method === "GET"
      ? { Accept: "application/json" }
      : undefined,
  });
  if (!response.ok) {
    throw new Error(`${url} returned HTTP ${response.status}`);
  }
  return response;
}

export async function verifyNftMetadataDocument(
  metadataUri,
  { expectedName, fetchImpl = fetch } = {},
) {
  if (typeof fetchImpl !== "function") {
    throw new Error("This browser cannot verify public NFT metadata");
  }
  const publicMetadataUri = requireHttpsUrl(metadataUri, "Metadata URI");
  const response = await fetchPublicAsset(fetchImpl, publicMetadataUri, "GET");
  const contentType = String(response.headers.get("content-type") || "").toLowerCase();
  if (!contentType.includes("application/json")) {
    throw new Error("The metadata URL is not served as application/json");
  }

  const metadata = await response.json();
  if (!metadata || typeof metadata !== "object" || Array.isArray(metadata)) {
    throw new Error("The metadata response is not a JSON object");
  }
  const name = String(metadata.name || "").trim();
  if (!name) throw new Error("The metadata name is empty");
  if (expectedName !== undefined && name !== String(expectedName).trim()) {
    throw new Error(`Metadata name mismatch: expected "${String(expectedName).trim()}"`);
  }
  if (metadata.description !== undefined && typeof metadata.description !== "string") {
    throw new Error("The metadata description must be a string");
  }
  if (metadata.attributes !== undefined && !Array.isArray(metadata.attributes)) {
    throw new Error("The metadata attributes must be an array");
  }

  const imageUrl = requireHttpsUrl(metadata.image, "Metadata image");
  const imageResponse = await fetchPublicAsset(fetchImpl, imageUrl, "HEAD");
  const imageType = String(imageResponse.headers.get("content-type") || "")
    .split(";", 1)[0]
    .toLowerCase();
  if (!imageType.startsWith("image/")) {
    throw new Error(`The metadata image is served as ${imageType || "an unknown type"}`);
  }

  if (metadata.animation_url !== undefined) {
    const animationUrl = requireHttpsUrl(metadata.animation_url, "animation_url");
    await fetchPublicAsset(fetchImpl, animationUrl, "HEAD");
  }

  return metadata;
}

export async function verifyNftMetadataBatch(
  items,
  { concurrency = 4, fetchImpl = fetch } = {},
) {
  const queue = Array.from(items || []);
  if (queue.length === 0) throw new Error("No metadata was supplied for minting");
  let cursor = 0;
  const workerCount = Math.max(1, Math.min(Number(concurrency) || 1, queue.length));

  async function worker() {
    while (cursor < queue.length) {
      const index = cursor;
      cursor += 1;
      const item = typeof queue[index] === "string"
        ? { uri: queue[index] }
        : queue[index];
      await verifyNftMetadataDocument(item.uri, {
        expectedName: item.expectedName,
        fetchImpl,
      });
    }
  }

  await Promise.all(Array.from({ length: workerCount }, worker));
}
